import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { articleService } from '../../../api/articleService';
import ViewArticleModal from '../../../components/modals/ViewArticleModal';
import {
  buildKbAccessCategories,
  computeKbStats,
  extractArticleDetail,
  isPublishedKbArticle,
  mapKbArticleForPortal,
  parsePublishedKbList,
  resolveKbTenantId,
  toViewArticleModalShape,
} from '../../../utils/knowledgeArticleDisplay';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Input } from '../../../components/ui';
import {
  Search, BookOpen, Star, Eye, Bookmark, Clock, Filter,
  ChevronRight, ChevronDown, Calendar, Tag, ArrowRight, RefreshCw, AlertCircle
} from 'lucide-react';

const KnowledgeBaseAccess = () => {
  const [articles, setArticles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewArticle, setViewArticle] = useState(null);
  const [viewArticleOpen, setViewArticleOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const searchSeqRef = useRef(0);

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await articleService.getArticles({
        includeDrafts: false,
        tenantId: resolveKbTenantId(),
      });
      setArticles(parsePublishedKbList(response));
    } catch (err) {
      setError('Failed to fetch knowledge base data.');
      console.error('Error fetching knowledge base data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const categories = useMemo(() => buildKbAccessCategories(articles), [articles]);
  const knowledgeBaseStats = useMemo(() => computeKbStats(articles), [articles]);

  const runSearch = async (query) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const seq = ++searchSeqRef.current;

    setSearchQuery(trimmed);
    setLastSearchQuery(trimmed);
    setActiveTab('search');
    setSearchLoading(true);
    setError(null);

    try {
      const response = await articleService.searchArticles(trimmed, {
        tenantId: resolveKbTenantId(),
        includeDrafts: false,
      });
      if (seq !== searchSeqRef.current) return;
      setSearchResults(parsePublishedKbList(response));
      try {
        const saved = localStorage.getItem('kb_search_history');
        const history = saved ? JSON.parse(saved) : [];
        if (!history.includes(trimmed)) {
          localStorage.setItem('kb_search_history', JSON.stringify([trimmed, ...history].slice(0, 10)));
        }
      } catch {
        // ignore localStorage errors
      }
    } catch (err) {
      console.error('KB search failed:', err);
      if (seq !== searchSeqRef.current) return;
      setSearchResults([]);
      setError('Search failed. Please try again.');
    } finally {
      if (seq === searchSeqRef.current) {
        setSearchLoading(false);
      }
    }
  };

  const handleSearchInput = (query) => {
    setSearchQuery(query);
  };

  const handleSearchSubmit = () => {
    runSearch(searchQuery);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleOpenArticle = async (article) => {
    const id = article?.articleId ?? article?.id;
    if (!id) return;

    setDetailLoading(true);
    try {
      const response = await articleService.getArticleById(id);
      const raw = extractArticleDetail(response);
      if (!raw || !isPublishedKbArticle(raw)) {
        setError('Article is not available.');
        return;
      }
      const mapped = mapKbArticleForPortal(raw);
      setViewArticle(toViewArticleModalShape(mapped));
      setViewArticleOpen(true);
      articleService.incrementUsedCount(id).catch(() => {});
    } catch (err) {
      console.error('Failed to load article:', err);
      setError('Failed to load article.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBookmarkToggle = (articleId) => {
    alert(`Bookmark toggled for article ${articleId}`);
  };

  const toggleCategoryExpansion = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const browseArticles = useMemo(() => {
    let list = [...articles];
    if (selectedCategory !== 'all') {
      list = list.filter((a) => a.categoryKey === selectedCategory);
    }
    return list;
  }, [articles, selectedCategory]);

  const popularArticles = useMemo(
    () => [...browseArticles].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5),
    [browseArticles]
  );

  const recentArticles = useMemo(
    () =>
      [...browseArticles]
        .sort((a, b) => new Date(b.lastUpdated || 0) - new Date(a.lastUpdated || 0))
        .slice(0, 5),
    [browseArticles]
  );

  const searchTabArticles = lastSearchQuery ? searchResults : [];

  const searchHistorySuggestions = useMemo(() => {
    try {
      const saved = localStorage.getItem('kb_search_history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, [lastSearchQuery]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatViews = (views) => {
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}k`;
    }
    return views.toString();
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Star key="half" className="h-4 w-4 text-yellow-400 fill-current opacity-50" />);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }
    
    return stars;
  };

  if (loading) {
    return <p className="p-6">Loading knowledge base...</p>;
  }

  if (error && articles.length === 0 && !loading) {
    return <p className="p-6 text-red-500">Error: {error}</p>;
  }

  const tabs = [
    { id: 'browse', label: 'Browse', icon: BookOpen },
    { id: 'search', label: 'Search', icon: Search },
    { id: 'bookmarks', label: 'Bookmarks', icon: Bookmark },
    { id: 'history', label: 'History', icon: Clock }
  ];

  const bookmarks = [];
  const history = [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
            Knowledge Base
          </h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark">
            Find solutions to common problems and learn how to use our systems
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2">
            <Filter size={18} />
            <span>Filters</span>
          </Button>
          <Button variant="outline" onClick={loadArticles} className="flex items-center space-x-2">
            <RefreshCw size={18} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {error && articles.length > 0 && (
        <div
          className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700 dark:text-red-400"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Articles</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {knowledgeBaseStats.totalArticles}
                </p>
              </div>
              <BookOpen className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Views</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {formatViews(knowledgeBaseStats.totalViews)}
                </p>
              </div>
              <Eye className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Avg Rating</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {knowledgeBaseStats.averageRating}/5
                </p>
              </div>
              <Star className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Success Rate</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {knowledgeBaseStats.searchSuccessRate}%
                </p>
              </div>
              <Search className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-secondary-light dark:text-text-secondary-dark" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={handleSearchSubmit}>Search</Button>
          </div>
          {searchHistorySuggestions.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">Recent searches:</p>
              <div className="flex flex-wrap gap-2">
                {searchHistorySuggestions.slice(0, 5).map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => runSearch(suggestion)}
                    className="text-xs"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="border-b border-border-light dark:border-border-dark">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'browse' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Categories */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {categories.length === 0 ? (
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    No categories available yet.
                  </p>
                ) : (
                  categories.map((category) => (
                  <div key={category.id} className="space-y-1">
                    <button
                      onClick={() => {
                        handleCategorySelect(category.id);
                        toggleCategoryExpansion(category.id);
                      }}
                      className={`w-full flex items-center justify-between p-2 rounded-md hover:bg-surface-light dark:hover:bg-surface-dark ${
                        selectedCategory === category.id ? 'bg-primary-50 dark:bg-primary-900' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <BookOpen className="h-4 w-4" />
                        <span className="text-sm font-medium">{category.name}</span>
                        <Badge variant="secondary">{category.articleCount}</Badge>
                      </div>
                      {category.subcategories.length > 0 && (
                        expandedCategories.has(category.id) ? 
                          <ChevronDown className="h-4 w-4" /> : 
                          <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    {expandedCategories.has(category.id) && category.subcategories.length > 0 && (
                      <div className="ml-6 space-y-1">
                        {category.subcategories.map((subcategory) => (
                          <button
                            key={subcategory.id}
                            onClick={() => handleCategorySelect(subcategory.id)}
                            className={`w-full flex items-center justify-between p-2 rounded-md hover:bg-surface-light dark:hover:bg-surface-dark text-sm ${
                              selectedCategory === subcategory.id ? 'bg-primary-50 dark:bg-primary-900' : ''
                            }`}
                          >
                            <span>{subcategory.name}</span>
                            <Badge variant="outline">{subcategory.articleCount}</Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Articles */}
          <div className="lg:col-span-2 space-y-6">
            {/* Popular Articles */}
            <Card>
              <CardHeader>
                <CardTitle>Popular Articles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {popularArticles.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                          {article.title}
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          <div className="flex items-center space-x-1">
                            <Eye className="h-3 w-3" />
                            <span>{formatViews(article.views)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {renderStars(article.rating)}
                            <span>({article.rating})</span>
                          </div>
                          <Badge variant="outline">{article.category}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBookmarkToggle(article.id)}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenArticle(article)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Articles */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Articles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentArticles.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                          {article.title}
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(article.lastUpdated)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {renderStars(article.rating)}
                            <span>({article.rating})</span>
                          </div>
                          <Badge variant="outline">{article.category}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBookmarkToggle(article.id)}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenArticle(article)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'search' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Search Results</CardTitle>
            </CardHeader>
            <CardContent>
              {searchLoading ? (
                <p className="text-center py-8 text-text-secondary-light dark:text-text-secondary-dark">
                  Searching articles...
                </p>
              ) : searchTabArticles.length > 0 ? (
                <div className="space-y-4">
                  {searchTabArticles.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                          {article.title}
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          <div className="flex items-center space-x-1">
                            <Eye className="h-3 w-3" />
                            <span>{formatViews(article.views)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {renderStars(article.rating)}
                            <span>({article.rating})</span>
                          </div>
                          <Badge variant="outline">{article.category}</Badge>
                          <div className="flex items-center space-x-1">
                            {article.tags.slice(0, 3).map((tag, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                <Tag className="h-2 w-2 mr-1" />
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBookmarkToggle(article.id)}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenArticle(article)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-text-secondary-light dark:text-text-secondary-dark mx-auto mb-4" />
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    {lastSearchQuery ? 'No articles found matching your search.' : 'Enter a search term to find articles.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'bookmarks' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Bookmarked Articles</CardTitle>
            </CardHeader>
            <CardContent>
              {bookmarks.length > 0 ? (
                <div className="space-y-4">
                  {bookmarks.map((bookmark) => {
                    const article = articles.find(a => a.id === bookmark.articleId);
                    if (!article) return null;
                    
                    return (
                      <div key={bookmark.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                        <div className="flex-1">
                          <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                            {article.title}
                          </h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                            {article.summary}
                          </p>
                          {bookmark.notes && (
                            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark italic">
                              Note: {bookmark.notes}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark mt-2">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>Bookmarked {formatDate(bookmark.createdAt)}</span>
                            </div>
                            <Badge variant="outline">{article.category}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBookmarkToggle(article.id)}
                          >
                            <Bookmark className="h-4 w-4 fill-current" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenArticle(article)}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Bookmark className="h-12 w-12 text-text-secondary-light dark:text-text-secondary-dark mx-auto mb-4" />
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    No bookmarked articles yet. Bookmark articles you find helpful!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recently Viewed Articles</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-4">
                  {history.map((historyItem) => {
                    const article = articles.find(a => a.id === historyItem.articleId);
                    if (!article) return null;
                    
                    return (
                      <div key={historyItem.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                        <div className="flex-1">
                          <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                            {article.title}
                          </h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                            {article.summary}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>Viewed {formatDate(historyItem.viewedAt)}</span>
                            </div>
                            <Badge variant="outline">{article.category}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBookmarkToggle(article.id)}
                          >
                            <Bookmark className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenArticle(article)}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-text-secondary-light dark:text-text-secondary-dark mx-auto mb-4" />
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    No recently viewed articles. Start browsing to see your history here!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <ViewArticleModal
        isOpen={viewArticleOpen}
        onClose={() => {
          setViewArticleOpen(false);
          setViewArticle(null);
        }}
        article={viewArticle}
      />

      {detailLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary bg-white p-4 rounded-full" />
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseAccess;
